import { ExposureStatus } from '~/types/person/details';

/**
 * The removal story every exposure surface tells: we found you on this broker, we asked them to
 * remove you, they closed it out. Four views render that story — the details table and its mobile
 * cards, the drawer's timeline and the company sidebar's tracker — and each one used to re-derive
 * it from `status`, so a new status landed in some of them and not others.
 *
 * `not-reached` is a stage that has not happened; `in-progress` is the one currently running.
 * A failed removal reaches the closed stage but lands on `failed`: the request was sent and
 * answered, just not in the member's favour.
 */
export type ExposureStageState = 'complete' | 'in-progress' | 'failed' | 'not-reached';

export interface ExposureProgress {
    /** 1 discovered, 2 removal requested, 3 settled. The trackers colour their icons from this. */
    step: 1 | 2 | 3;
    isFailed: boolean;
    detected: ExposureStageState;
    requestSent: ExposureStageState;
    closed: ExposureStageState;
    /**
     * Every stage's state is carried by colour alone — a dot, a tick, a bar — so each stage
     * also needs the same fact in words for a screen reader.
     */
    labels: {
        detected: string;
        requestSent: string;
        closed: string;
    };
}

const STAGE_LABEL: Record<ExposureStageState, string> = {
    complete: ', complete',
    'in-progress': ', in progress',
    failed: ', removal failed',
    'not-reached': ', not reached',
};

export const exposureProgress = (exposure?: { status?: ExposureStatus | string | null } | null): ExposureProgress => {
    const status = exposure?.status;

    const stages = ((): Pick<ExposureProgress, 'step' | 'isFailed' | 'detected' | 'requestSent' | 'closed'> => {
        switch (status) {
            case ExposureStatus.NOT_FOUND:
                return { step: 1, isFailed: false, detected: 'not-reached', requestSent: 'not-reached', closed: 'not-reached' };
            case ExposureStatus.IN_PROGRESS:
                return { step: 2, isFailed: false, detected: 'complete', requestSent: 'in-progress', closed: 'not-reached' };
            case ExposureStatus.CLOSED:
                return { step: 3, isFailed: false, detected: 'complete', requestSent: 'complete', closed: 'complete' };
            case ExposureStatus.FAILED:
                return { step: 3, isFailed: true, detected: 'complete', requestSent: 'complete', closed: 'failed' };
            // Exposed, pending, and anything the API adds later: found, nothing asked yet.
            default:
                return { step: 1, isFailed: false, detected: 'complete', requestSent: 'not-reached', closed: 'not-reached' };
        }
    })();

    return {
        ...stages,
        labels: {
            detected: STAGE_LABEL[stages.detected],
            requestSent: STAGE_LABEL[stages.requestSent],
            closed: STAGE_LABEL[stages.closed],
        },
    };
};
